'use client';

import { useState, useEffect } from 'react';
import { apiService } from '@/lib/api';

interface TimezoneData {
  timezone: string;
  country: string;
  countryCode: string;
  city: string;
  isVpn: boolean;
}

export function useTimezone() {
  const [timezoneData, setTimezoneData] = useState<TimezoneData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vpnDetected, setVpnDetected] = useState(false);

  useEffect(() => {
    loadTimezone();
  }, []);

  const loadTimezone = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check VPN first
      try {
        const vpnCheck: any = await apiService.checkVPN();
        if (vpnCheck?.success && vpnCheck?.data?.isVpn) {
          setVpnDetected(true);
        }
      } catch (vpnErr) {
        // VPN check failed, continue anyway
        console.warn('VPN check failed:', vpnErr);
      }

      // Get timezone
      const response: any = await apiService.getUserTimezone();
      if (response?.success && response?.data) {
        setTimezoneData(response.data);
        if (response.data.isVpn) {
          setVpnDetected(true);
        }
      } else {
        // Fallback to browser timezone
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezoneData({
          timezone: browserTimezone,
          country: 'Unknown',
          countryCode: 'XX',
          city: 'Unknown',
          isVpn: false
        });
      }
    } catch (err: any) {
      console.error('Failed to load timezone:', err);
      setError(err.message || 'Failed to detect timezone');
      
      // Fallback to browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setTimezoneData({
        timezone: browserTimezone,
        country: 'Unknown',
        countryCode: 'XX',
        city: 'Unknown',
        isVpn: false
      });
    } finally {
      setLoading(false);
    }
  };

  const retry = () => {
    loadTimezone();
  };

  return {
    timezoneData,
    loading,
    error,
    vpnDetected,
    retry
  };
}

